import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, RefreshCw, RotateCcw, Save, Calculator, Info, TrendingUp, AlertTriangle } from 'lucide-react';
import { useDebouncedCallback } from 'use-debounce';
import { toast } from 'sonner';
import { api } from '../../api/client';

// UI Components
import { Button } from './button';
import { Badge } from './badge';
import { Input } from './input';
import { Slider } from './slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';
import { Card } from './card';

/**
 * PricingDrawer - Comprehensive pricing management interface
 * 
 * Features:
 * - Right-side sliding drawer (600px width)
 * - 5 main sections: Product Info, Cost Breakdown, Wholesale/Retail, Actions, Audit
 * - Real-time calculations with debounced updates
 * - Dark mode support
 * - Validation and error handling
 */
const PricingDrawer = ({ 
  isOpen, 
  onClose, 
  productData, 
  onSave, 
  onUpdate 
}) => {
  // State management
  const [drawerData, setDrawerData] = useState(null);
  const [costs, setCosts] = useState({
    manufacturing_cost: 0,
    freight_cost: 0,
    duties_cost: 0,
    handling_cost: 0,
    overhead_allocation: 0,
    ad_spend_allocation: 0,
    fulfillment_cost: 0,
    // Manufacturing breakdown
    raw_materials: 0,
    labor_cost: 0,
    factory_overhead: 0
  });
  const [wholesaleMargin, setWholesaleMargin] = useState(25.0);
  const [retailMargin, setRetailMargin] = useState(45.0);
  const [pricingTier, setPricingTier] = useState('both');
  const [calculatedValues, setCalculatedValues] = useState({});
  const [changedFields, setChangedFields] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  const [manufacturingBreakdown, setManufacturingBreakdown] = useState(null);
  const [manufacturingHistory, setManufacturingHistory] = useState([]);
  const [loadingManufacturing, setLoadingManufacturing] = useState(false);

  // Initialize data when drawer opens
  useEffect(() => {
    if (isOpen && productData) {
      initializeDrawerData(productData);
    }
  }, [isOpen, productData]);

  const initializeDrawerData = (data) => {
    setDrawerData(data);
    
    // Initialize costs
    setCosts({
      manufacturing_cost: data.manufacturing_cost || 0,
      freight_cost: data.freight_cost || 0,
      duties_cost: data.duties_cost || 0,
      handling_cost: data.handling_cost || 0,
      overhead_allocation: data.overhead_allocation || 0,
      ad_spend_allocation: data.ad_spend_allocation || 0,
      fulfillment_cost: data.fulfillment_cost || 0,
      // Manufacturing breakdown
      raw_materials: data.raw_materials || 0,
      labor_cost: data.labor_cost || 0,
      factory_overhead: data.factory_overhead || 0
    });

    // Initialize margins and tier
    setWholesaleMargin(data.wholesale_margin_percent || 25.0);
    setRetailMargin(data.retail_margin_percent || 45.0);
    setPricingTier(data.pricing_tier || 'both');
    
    // Clear changed fields
    setChangedFields({});
    
    // Fetch manufacturing breakdown
    fetchManufacturingBreakdown(data.sku);
  };

  // Fetch manufacturing breakdown from API
  const fetchManufacturingBreakdown = async (sku) => {
    if (!sku) return;
    
    setLoadingManufacturing(true);
    try {
      const response = await api.get(`/api/v1/manufacturing-costs/${sku}`);
      
      if (response.status === 200) {
        const data = response.data;
        if (data.current_cost) {
          setManufacturingBreakdown(data.current_cost);
          
          // Update costs state with manufacturing breakdown
          setCosts(prev => ({
            ...prev,
            raw_materials: data.current_cost.raw_materials || 0,
            labor_cost: data.current_cost.labor_cost || 0,
            factory_overhead: data.current_cost.factory_overhead || 0,
            manufacturing_cost: data.current_cost.total_manufacturing_cost || 0
          }));
          
          console.log('✅ Manufacturing breakdown loaded:', data.current_cost);
        } else {
          console.log('ℹ️ No manufacturing breakdown available for', sku);
          setManufacturingBreakdown(null);
        }
      } else {
        console.error('❌ Failed to fetch manufacturing breakdown:', response.statusText);
      }
    } catch (error) {
      console.error('❌ Error fetching manufacturing breakdown:', error);
    } finally {
      setLoadingManufacturing(false);
    }
  };

  // Fetch manufacturing cost history
  const fetchManufacturingHistory = async (sku) => {
    if (!sku) return;
    
    try {
      const response = await api.get(`/api/v1/manufacturing-costs/${sku}/history?limit=10`);
      
      if (response.status === 200) {
        const data = response.data;
        setManufacturingHistory(data.history || []);
        console.log('✅ Manufacturing history loaded:', data.history?.length || 0, 'records');
        
        // Show a toast notification
        if (window.toast) {
          window.toast.success(`Loaded ${data.history?.length || 0} manufacturing cost history records`);
        }
      } else {
        console.error('❌ Failed to fetch manufacturing history:', response.statusText);
      }
    } catch (error) {
      console.error('❌ Error fetching manufacturing history:', error);
    }
  };

  // Real-time calculation with debounce
  const calculatePricing = useDebouncedCallback((costData, margins, tier) => {
    // Calculate total cost
    const totalCost = 
      (costData.manufacturing_cost || 0) +
      (costData.freight_cost || 0) +
      (costData.duties_cost || 0) +
      (costData.handling_cost || 0) +
      (costData.overhead_allocation || 0) +
      (costData.ad_spend_allocation || 0) +
      (costData.fulfillment_cost || 0);

    // Calculate manufacturing total
    const manufacturingTotal = 
      (costData.raw_materials || 0) +
      (costData.labor_cost || 0) +
      (costData.factory_overhead || 0);

    // Calculate wholesale price
    const wholesalePrice = margins.wholesale > 0 && margins.wholesale < 100
      ? totalCost / (1 - margins.wholesale / 100)
      : 0;

    // Calculate retail price
    const retailPrice = margins.retail > 0 && margins.retail < 100
      ? totalCost / (1 - margins.retail / 100)
      : 0;

    // Calculate net margins
    const wholesaleNetMargin = wholesalePrice > 0
      ? ((wholesalePrice - totalCost) / wholesalePrice * 100)
      : 0;

    const retailNetMargin = retailPrice > 0
      ? ((retailPrice - totalCost) / retailPrice * 100)
      : 0;

    // Calculate markup and difference
    const priceDifference = retailPrice - wholesalePrice;
    const markupPercent = wholesalePrice > 0
      ? (priceDifference / wholesalePrice * 100)
      : 0;

    // Calculate system price (using target margin)
    const targetMargin = drawerData?.target_margin_percent || 40;
    const systemPrice = targetMargin > 0 && targetMargin < 100
      ? totalCost / (1 - targetMargin / 100)
      : 0;

    setCalculatedValues({
      totalCost,
      manufacturingTotal,
      wholesalePrice,
      retailPrice,
      wholesaleNetMargin,
      retailNetMargin,
      priceDifference,
      markupPercent,
      systemPrice
    });
  }, 300);

  // Trigger calculation when values change
  useEffect(() => {
    calculatePricing(costs, { wholesale: wholesaleMargin, retail: retailMargin }, pricingTier);
  }, [costs, wholesaleMargin, retailMargin, pricingTier, calculatePricing]);

  // Cost update handlers
  const updateCost = useCallback((field, value) => {
    const numValue = parseFloat(value) || 0;
    setCosts(prev => ({ ...prev, [field]: numValue }));
    setChangedFields(prev => ({ ...prev, [field]: numValue }));
  }, []);

  // Margin update handlers
  const handleWholesaleMarginChange = useCallback((value) => {
    setWholesaleMargin(value);
    setChangedFields(prev => ({ ...prev, wholesale_margin_percent: value }));
  }, []);

  const handleRetailMarginChange = useCallback((value) => {
    setRetailMargin(value);
    setChangedFields(prev => ({ ...prev, retail_margin_percent: value }));
  }, []);

  // Pricing tier change handler
  const handlePricingTierChange = useCallback((tier) => {
    setPricingTier(tier);
    setChangedFields(prev => ({ ...prev, pricing_tier: tier }));
    
    // Auto-adjust margins based on tier
    if (tier === 'wholesale') {
      setRetailMargin(0);
      setChangedFields(prev => ({ ...prev, retail_margin_percent: 0 }));
    } else if (tier === 'retail') {
      setWholesaleMargin(0);
      setChangedFields(prev => ({ ...prev, wholesale_margin_percent: 0 }));
    }
  }, []);

  // Action handlers
  const handleRecalculate = async () => {
    if (!drawerData?.sku) return;
    
    setIsCalculating(true);
    try {
      const response = await api.post(`/api/v1/product_pricing/${drawerData.sku}/recalculate`, null, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        }
      });
      
      if (response.status === 200) {
        const updated = response.data;
        initializeDrawerData(updated.pricing);
        toast.success('Pricing recalculated successfully');
      }
    } catch (error) {
      console.error('Recalculate error:', error);
      toast.error('Failed to recalculate pricing');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleResetToSystem = async () => {
    if (!confirm('Reset to system-calculated pricing? Manual overrides will be lost.')) {
      return;
    }

    try {
      const response = await api.post(`/api/v1/product_pricing/${drawerData.sku}`, {
        pricing_mode: 'system',
        final_price: null,
        change_reason: 'Reset to system pricing via drawer'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        }
      });

      if (response.status === 200) {
        const updated = response.data;
        initializeDrawerData(updated);
        toast.success('Reset to system pricing');
      }
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('Failed to reset pricing');
    }
  };

  // Validation function as per requirements
  const validateMargins = useCallback((wholesaleMargin, retailMargin) => {
    const errors = [];
    const warnings = [];
    
    // Wholesale validation
    if (wholesaleMargin < 0 || wholesaleMargin > 50) {
      errors.push("Wholesale margin must be between 0-50%");
    }
    if (wholesaleMargin > 0 && wholesaleMargin < 15) {
      warnings.push("Low wholesale margin (<15%) - may not cover operational costs");
    }
    if (wholesaleMargin > 35) {
      warnings.push("High wholesale margin (>35%) - may reduce competitiveness");
    }
    
    // Retail validation
    if (retailMargin < 0 || retailMargin > 70) {
      errors.push("Retail margin must be between 0-70%");
    }
    if (retailMargin > 0 && retailMargin < 35) {
      warnings.push("Low retail margin (<35%) - ensure profitability");
    }
    if (retailMargin > 60) {
      warnings.push("High retail margin (>60%) - may reduce competitiveness");
    }
    
    // Price relationship validation
    const totalCost = calculatedValues.totalCost || 0;
    const wholesalePrice = totalCost > 0 && wholesaleMargin > 0 && wholesaleMargin < 100
      ? totalCost / (1 - wholesaleMargin / 100) 
      : 0;
    const retailPrice = totalCost > 0 && retailMargin > 0 && retailMargin < 100
      ? totalCost / (1 - retailMargin / 100)
      : 0;
    
    if (retailPrice > 0 && wholesalePrice > 0 && retailPrice < wholesalePrice) {
      errors.push("Retail price cannot be lower than wholesale price");
    }
    
    return { errors, warnings, isValid: errors.length === 0 };
  }, [calculatedValues.totalCost]);

  const handleSave = async () => {
    if (!drawerData?.sku || Object.keys(changedFields).length === 0) {
      toast.info('No changes to save');
      return;
    }

    // Validate first
    const validation = validateMargins(wholesaleMargin, retailMargin);
    
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }
    
    // Show warnings but allow save
    validation.warnings.forEach(warning => toast.warning(warning));

    setIsSaving(true);
    try {
      const response = await api.post(`/api/v1/product_pricing/${drawerData.sku}`, {
        ...changedFields,
        change_reason: 'Updated via Pricing Drawer'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        }
      });

      if (response.status !== 200) throw new Error('Save failed');

      const updated = response.data;
      
      // Update Smart Grid if callback provided
      if (onSave) {
        onSave(drawerData.sku, updated);
      }
      
      toast.success('Pricing saved successfully');
      onClose();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Format helpers
  const formatCurrency = (value) => `$${(value || 0).toFixed(2)}`;
  const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (!isOpen || !drawerData) return null;

  const hasChanges = Object.keys(changedFields).length > 0;

  return createPortal(
    <TooltipProvider>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className={`
        fixed top-0 right-0 h-full w-[600px] max-w-full
        bg-white dark:bg-gray-900 
        shadow-2xl z-50
        transform transition-transform duration-300 ease-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        pricing-drawer
      `}>
        {/* We'll add the drawer content sections in the next step */}
        <div className="h-full flex flex-col">
          {/* Header - will be implemented next */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 pricing-drawer-header">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {drawerData.sku}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {drawerData.product_name || 'Product Name'}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              
              {/* SECTION 1: Product Info Header (Read-Only) */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 dark:text-gray-400">Category:</span>
                    <Badge variant="secondary">{drawerData.category || 'Uncategorized'}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 dark:text-gray-400">Status:</span>
                    <Badge variant={drawerData.status === 'active' ? 'default' : 'secondary'}>
                      {drawerData.status || 'Unknown'}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Total Cost:</span>
                    <span className="ml-2 font-mono font-bold text-blue-600 dark:text-blue-400">
                      {formatCurrency(calculatedValues.totalCost)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Last Updated:</span>
                    <span className="ml-2">{formatDate(drawerData.updated_at)}</span>
                  </div>
                </div>
              </div>

              {/* SECTION 2: Cost Breakdown (Editable) */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-lg">💰</span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Cost Breakdown</h3>
                </div>
                
                {/* Manufacturing Costs Subsection */}
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg cost-breakdown-section">
                  <h4 className="font-semibold mb-3 flex items-center gap-2 text-blue-700 dark:text-blue-300">
                    🏭 Manufacturing Costs
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="w-4 h-4 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        Breakdown of production costs
                      </TooltipContent>
                    </Tooltip>
                  </h4>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2 px-3 rounded hover:bg-blue-100 dark:hover:bg-blue-800/30">
                      <span className="text-sm font-medium">Raw Materials</span>
                      <div className="w-28">
                        <Input
                          type="number"
                          value={costs.raw_materials || 0}
                          onChange={(e) => updateCost('raw_materials', e.target.value)}
                          className="text-right text-sm h-8"
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between py-2 px-3 rounded hover:bg-blue-100 dark:hover:bg-blue-800/30">
                      <span className="text-sm font-medium">Labor Cost</span>
                      <div className="w-28">
                        <Input
                          type="number"
                          value={costs.labor_cost || 0}
                          onChange={(e) => updateCost('labor_cost', e.target.value)}
                          className="text-right text-sm h-8"
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between py-2 px-3 rounded hover:bg-blue-100 dark:hover:bg-blue-800/30">
                      <span className="text-sm font-medium">Factory Overhead</span>
                      <div className="w-28">
                        <Input
                          type="number"
                          value={costs.factory_overhead || 0}
                          onChange={(e) => updateCost('factory_overhead', e.target.value)}
                          className="text-right text-sm h-8"
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                    
                    <div className="border-t border-blue-200 dark:border-blue-700 pt-2 mt-3">
                      <div className="flex justify-between items-center">
                        <div className="flex flex-col">
                          <span className="font-semibold text-blue-700 dark:text-blue-300">Manufacturing Total:</span>
                          {manufacturingBreakdown && (
                            <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                              Effective: {formatDate(manufacturingBreakdown.effective_date)}
                              {manufacturingBreakdown.calculation_method === 'manual' && ' (Manual Entry)'}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-blue-700 dark:text-blue-300">
                            {formatCurrency(calculatedValues.manufacturingTotal)}
                          </span>
                          {loadingManufacturing && (
                            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          )}
                        </div>
                      </div>
                      
                      {manufacturingBreakdown && (
                        <div className="mt-2 flex justify-between items-center">
                          <div className="text-xs text-gray-500">
                            {manufacturingBreakdown.notes && (
                              <span>"{manufacturingBreakdown.notes}"</span>
                            )}
                          </div>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => fetchManufacturingHistory(drawerData?.sku)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 h-6 px-2 text-xs"
                          >
                            View History →
                          </Button>
                        </div>
                      )}
                      
                      {!manufacturingBreakdown && !loadingManufacturing && (
                        <div className="text-xs text-gray-500 italic mt-2">
                          No detailed manufacturing cost breakdown available
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Other Costs */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-1">
                    📦 Other Costs
                  </h4>
                  
                  {[
                    { field: 'freight_cost', label: 'Freight', icon: '🚛' },
                    { field: 'duties_cost', label: 'Duties', icon: '📋' },
                    { field: 'handling_cost', label: 'Handling', icon: '📦' },
                    { field: 'overhead_allocation', label: 'Overhead Allocation', icon: '🏢' },
                    { field: 'ad_spend_allocation', label: 'Ad Spend', icon: '📢' },
                    { field: 'fulfillment_cost', label: 'Fulfillment', icon: '🚀' }
                  ].map(({ field, label, icon }) => (
                    <div key={field} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{icon}</span>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
                      </div>
                      <div className="w-28">
                        <Input
                          type="number"
                          value={costs[field] || 0}
                          onChange={(e) => updateCost(field, e.target.value)}
                          className="text-right text-sm h-8"
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Total Cost Display */}
                <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-700 dark:text-gray-300">TOTAL COST</span>
                    <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {formatCurrency(calculatedValues.totalCost)}
                    </span>
                  </div>
                </div>
              </div>

              {/* SECTION 3: Wholesale vs Retail Pricing */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-lg">💵</span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Wholesale vs Retail Pricing</h3>
                </div>
                
                {/* Pricing Tier Selector */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Pricing Channel
                  </label>
                  <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
                    {[
                      { value: 'wholesale', label: '🏢 Wholesale Only' },
                      { value: 'retail', label: '🛒 Retail Only' },
                      { value: 'both', label: '🔄 Both Channels' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handlePricingTierChange(option.value)}
                        className={`
                          flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200
                          ${pricingTier === option.value
                            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                          }
                        `}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Wholesale Pricing Card */}
                {(pricingTier === 'wholesale' || pricingTier === 'both') && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg wholesale-card">
                    <h4 className="font-semibold mb-4 flex items-center gap-2 text-blue-700 dark:text-blue-300">
                      🏢 WHOLESALE PRICING (B2B)
                    </h4>
                    
                    <div className="space-y-4">
                      {/* Margin Slider */}
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                          Target Margin %
                        </label>
                        <div className="flex items-center gap-4">
                          <Slider
                            value={[wholesaleMargin]}
                            onValueChange={(values) => handleWholesaleMarginChange(values[0])}
                            min={0}
                            max={50}
                            step={0.5}
                            className="flex-1"
                          />
                          <div className="w-20">
                            <Input
                              type="number"
                              value={wholesaleMargin}
                              onChange={(e) => handleWholesaleMarginChange(parseFloat(e.target.value) || 0)}
                              className="text-right text-sm h-8"
                              step={0.5}
                              min={0}
                              max={50}
                            />
                          </div>
                          <span className="text-sm text-gray-500 w-6">%</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Typical wholesale margins: 15-35%
                        </p>
                      </div>
                      
                      {/* Calculated Values */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-blue-100 dark:bg-blue-800/30 rounded-lg">
                          <div className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">
                            Wholesale Price
                          </div>
                          <div className="text-lg font-bold text-blue-700 dark:text-blue-300">
                            {formatCurrency(calculatedValues.wholesalePrice)}
                          </div>
                        </div>
                        <div className={`p-3 rounded-lg ${
                          calculatedValues.wholesaleNetMargin >= 25 ? 'bg-green-100 dark:bg-green-800/30 text-green-700 dark:text-green-300' :
                          calculatedValues.wholesaleNetMargin >= 15 ? 'bg-yellow-100 dark:bg-yellow-800/30 text-yellow-700 dark:text-yellow-300' :
                          'bg-red-100 dark:bg-red-800/30 text-red-700 dark:text-red-300'
                        }`}>
                          <div className="text-xs font-medium uppercase tracking-wide mb-1 opacity-80">
                            Net Margin
                          </div>
                          <div className="text-lg font-bold">
                            {formatPercent(calculatedValues.wholesaleNetMargin)}
                          </div>
                        </div>
                      </div>
                      
                      {/* Warning Messages */}
                      {wholesaleMargin < 15 && (
                        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                          <div className="flex items-start gap-2 text-yellow-800 dark:text-yellow-200">
                            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div className="text-sm">
                              ⚠️ Low margin warning: {wholesaleMargin}% is below recommended 15% minimum
                            </div>
                          </div>
                        </div>
                      )}
                      {wholesaleMargin > 35 && (
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <div className="flex items-start gap-2 text-blue-800 dark:text-blue-200">
                            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div className="text-sm">
                              💡 High margin: {wholesaleMargin}% may reduce competitiveness
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Retail Pricing Card */}
                {(pricingTier === 'retail' || pricingTier === 'both') && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 rounded-lg retail-card">
                    <h4 className="font-semibold mb-4 flex items-center gap-2 text-green-700 dark:text-green-300">
                      🛒 RETAIL PRICING (B2C)
                    </h4>
                    
                    <div className="space-y-4">
                      {/* Margin Slider */}
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                          Target Margin %
                        </label>
                        <div className="flex items-center gap-4">
                          <Slider
                            value={[retailMargin]}
                            onValueChange={(values) => handleRetailMarginChange(values[0])}
                            min={0}
                            max={70}
                            step={0.5}
                            className="flex-1"
                          />
                          <div className="w-20">
                            <Input
                              type="number"
                              value={retailMargin}
                              onChange={(e) => handleRetailMarginChange(parseFloat(e.target.value) || 0)}
                              className="text-right text-sm h-8"
                              step={0.5}
                              min={0}
                              max={70}
                            />
                          </div>
                          <span className="text-sm text-gray-500 w-6">%</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Typical retail margins: 35-60%
                        </p>
                      </div>
                      
                      {/* Calculated Values */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-green-100 dark:bg-green-800/30 rounded-lg">
                          <div className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wide mb-1">
                            Retail Price
                          </div>
                          <div className="text-lg font-bold text-green-700 dark:text-green-300">
                            {formatCurrency(calculatedValues.retailPrice)}
                          </div>
                        </div>
                        <div className={`p-3 rounded-lg ${
                          calculatedValues.retailNetMargin >= 40 ? 'bg-green-100 dark:bg-green-800/30 text-green-700 dark:text-green-300' :
                          calculatedValues.retailNetMargin >= 30 ? 'bg-yellow-100 dark:bg-yellow-800/30 text-yellow-700 dark:text-yellow-300' :
                          'bg-red-100 dark:bg-red-800/30 text-red-700 dark:text-red-300'
                        }`}>
                          <div className="text-xs font-medium uppercase tracking-wide mb-1 opacity-80">
                            Net Margin
                          </div>
                          <div className="text-lg font-bold">
                            {formatPercent(calculatedValues.retailNetMargin)}
                          </div>
                        </div>
                      </div>
                      
                      {/* Warning Messages */}
                      {retailMargin < 35 && (
                        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                          <div className="flex items-start gap-2 text-yellow-800 dark:text-yellow-200">
                            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div className="text-sm">
                              ⚠️ Low margin: {retailMargin}% is below recommended 35% minimum
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Price Comparison (only when both channels active) */}
                {pricingTier === 'both' && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-4 rounded-lg comparison-card">
                    <h4 className="font-semibold mb-3 text-center text-purple-700 dark:text-purple-300">Price Comparison</h4>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Wholesale</div>
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {formatCurrency(calculatedValues.wholesalePrice)}
                        </div>
                      </div>
                      <div className="flex items-center justify-center">
                        <div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Markup</div>
                          <div className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                            +{formatCurrency(calculatedValues.priceDifference)}
                          </div>
                          <div className="text-xs text-purple-500 dark:text-purple-400">
                            ({formatPercent(calculatedValues.markupPercent)})
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Retail</div>
                        <div className="text-xl font-bold text-green-600 dark:text-green-400">
                          {formatCurrency(calculatedValues.retailPrice)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* SECTION 4: Audit Trail & History */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-lg">📋</span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Audit Trail</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Last Updated:</span>
                    <span>{formatDate(drawerData.updated_at)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Updated By:</span>
                    <span className="font-medium">{drawerData.updated_by || 'System'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Pricing Mode:</span>
                    <Badge variant={drawerData.pricing_mode === 'system' ? 'default' : 'secondary'}>
                      {drawerData.pricing_mode || 'system'}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">System Price:</span>
                    <span className="font-mono">{formatCurrency(calculatedValues.systemPrice)}</span>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  className="w-full mt-3"
                  size="sm"
                  onClick={() => {
                    toast.info('Price history modal would open here');
                  }}
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  View Full Price History →
                </Button>
              </div>

            </div>
          </div>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-white dark:bg-gray-900 p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleRecalculate}
                disabled={isCalculating}
                size="sm"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isCalculating ? 'animate-spin' : ''}`} />
                Recalculate
              </Button>
              
              <Button
                variant="outline"
                onClick={handleResetToSystem}
                disabled={drawerData.pricing_mode === 'system'}
                size="sm"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
              
              <div className="flex-1" />
              
              <Button
                variant="ghost"
                onClick={onClose}
                size="sm"
              >
                Cancel
              </Button>
              
              <Button
                variant="default"
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                size="sm"
              >
                {isSaving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
            
            {hasChanges && (
              <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                You have unsaved changes
              </div>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>,
    document.body
  );
};

export default PricingDrawer;